import type { InfiniteData } from '@tanstack/react-query';

import type { StaffChatMessage, StaffChatMessagePage } from '@nucleus-commerce/api-client/admin';

/** Thread pages: index 0 is the oldest loaded page; the page param is the `before` cursor. */
export type ThreadData = InfiniteData<StaffChatMessagePage>;

// UUIDv7 ids sort by time, so the id is the order (D-103).
const byId = (a: StaffChatMessage, b: StaffChatMessage) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Every loaded message, oldest first, each id once. */
export function flattenThread(data: ThreadData | undefined): StaffChatMessage[] {
  if (!data) return [];
  const seen = new Set<string>();
  const out: StaffChatMessage[] = [];
  for (const page of data.pages) {
    for (const message of page.items) {
      if (seen.has(message.id)) continue;
      seen.add(message.id);
      out.push(message);
    }
  }
  return out.sort(byId);
}

export function lastMessageId(data: ThreadData | undefined): string | undefined {
  return flattenThread(data).at(-1)?.id;
}

/**
 * Merges arrivals (a REST answer, a socket frame, a poll, a replay) by id: a known id is replaced
 * in place, a new one goes into the newest page. Nothing is ever shown twice.
 */
export function mergeIntoThread(
  data: ThreadData | undefined,
  arrivals: readonly StaffChatMessage[],
): ThreadData | undefined {
  if (!data || arrivals.length === 0) return data;
  const incoming = new Map(arrivals.map((message) => [message.id, message]));
  const pages = data.pages.map((page) => {
    if (!page.items.some((message) => incoming.has(message.id))) return page;
    const items = page.items.map((message) => {
      const next = incoming.get(message.id);
      if (!next) return message;
      incoming.delete(message.id);
      return next;
    });
    return { ...page, items };
  });
  if (incoming.size > 0) {
    const last = pages.length - 1;
    const newest = pages[last];
    if (newest)
      pages[last] = { ...newest, items: [...newest.items, ...incoming.values()].sort(byId) };
  }
  return { ...data, pages };
}

/** A read receipt: our side's messages up to the id were read by the shopper at `at`. */
export function applyReadReceipt(
  data: ThreadData | undefined,
  upToMessageId: string,
  at: string,
): ThreadData | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((message) =>
        message.authorType !== 'visitor' && message.readAt === null && message.id <= upToMessageId
          ? { ...message, readAt: at }
          : message,
      ),
    })),
  };
}
